import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

type TierVariant = "default" | "secondary" | "outline";

const TIER_VARIANTS: Record<string, TierVariant> = {
  basic: "outline",
  pro: "default",
  enterprise: "secondary",
};

interface Subscription {
  planId?: string;
  planName?: string;
  tier?: string;
  priceEur?: number | null;
  unlockedFeatures?: string[];
  supportTier?: string;
  freeCreditsIncluded?: number;
  pendingPlanId?: string;
}

interface CurrentPlanCardProps {
  subscription: Subscription;
}

export function CurrentPlanCard({ subscription }: CurrentPlanCardProps) {
  const { t } = useTranslation();
  const {
    planId,
    planName,
    tier,
    priceEur,
    unlockedFeatures = [],
    supportTier,
    freeCreditsIncluded = 0,
    pendingPlanId,
  } = subscription;

  const priceLabel =
    priceEur != null
      ? t("credits.pricePerMonth", { price: priceEur })
      : t("credits.priceCustom");

  return (
    <Card>
      <CardContent className="flex flex-wrap items-start gap-6">
        <div className="min-w-[220px] flex-1">
          <p className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
            {t("credits.currentPlan")}
          </p>
          <div className="mt-1 flex items-center gap-2">
            <h2 className="text-xl font-semibold">{planName || planId}</h2>
            {tier && (
              <Badge variant={TIER_VARIANTS[tier] || "outline"}>
                {t(`credits.tier.${tier}`, { defaultValue: tier })}
              </Badge>
            )}
          </div>
          <p className="mt-1 text-sm text-muted-foreground">{priceLabel}</p>
          {pendingPlanId && (
            <p className="mt-1 text-xs text-amber-600">
              {t("credits.pendingPlanNotice", { planId: pendingPlanId })}
            </p>
          )}
        </div>

        <Separator
          orientation="vertical"
          className="hidden h-auto self-stretch md:block"
        />

        <div className="min-w-[260px] flex-[1.5]">
          <p className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
            {t("credits.unlockedFeatures")}
          </p>
          <div className="mt-1 flex flex-wrap gap-1">
            {unlockedFeatures.length > 0 ? (
              unlockedFeatures.map((f) => (
                <Badge key={f} variant="outline">
                  {t(`credits.unlocked.${f}`, { defaultValue: f })}
                </Badge>
              ))
            ) : (
              <span className="text-xs text-muted-foreground">
                {t("credits.noUnlockedFeatures")}
              </span>
            )}
          </div>
          <div className="mt-3 flex gap-6">
            {freeCreditsIncluded > 0 && (
              <div>
                <p className="text-xs text-muted-foreground">
                  {t("credits.freeCreditsIncluded")}
                </p>
                <p className="text-sm">{freeCreditsIncluded}</p>
              </div>
            )}
            {supportTier && (
              <div>
                <p className="text-xs text-muted-foreground">
                  {t("credits.support")}
                </p>
                <p className="text-sm">
                  {t(`credits.supportTier.${supportTier}`, {
                    defaultValue: supportTier,
                  })}
                </p>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
